import React, { ChangeEvent, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { checkChange } from "utils/checkChange";
import { useToast } from "hooks/useToast";
import { useHistory } from "react-router-dom";
import { useGraphQLAuth } from "hooks/useGraphQLAuth";
import { FileService } from "services/fileService";

export interface SocialMediaUrls {
  websiteUrl?: string;
  linkedinUrl?: string;
  twitterUrl?: string;
  instagramUrl?: string;
  facebookUrl?: string;
  youtubeUrl?: string;
  tiktokUrl?: string;
}

export interface EventProps {
  name: string;
  description: string;
  organizer: string;
  logo?: {
    signedFileUrl?: string;
  };
  address: string;
  type: "PUBLIC" | "PRIVATE";
  dateDescription: string;
  endDate?: string;
  dateComplement: string;
  maximumCapacity: number;
  isPublished: boolean;
  mentorshipsEnabled?: boolean;
  showTicketQrCode?: boolean;
  // Social media URLs
  socialMedia?: SocialMediaUrls;
  // Map support
  mapPdf?: {
    signedFileUrl?: string;
  };
  // Ticket purchase
  ticketPurchaseUrl?: string;
  // mapType?: string;
}

const initInputsValues: EventProps = {
  name: "",
  description: "",
  organizer: "",
  address: "",
  type: "PUBLIC",
  dateDescription: "",
  endDate: "",
  dateComplement: "",
  maximumCapacity: 0,
  isPublished: false,
  mentorshipsEnabled: false,
  showTicketQrCode: true,
  socialMedia: {
    websiteUrl: "",
    linkedinUrl: "",
    twitterUrl: "",
    instagramUrl: "",
    facebookUrl: "",
    youtubeUrl: "",
    tiktokUrl: "",
  },
  ticketPurchaseUrl: "",
};

const initTouchedInputsValues = {
  name: false,
  description: false,
  organizer: false,
  address: false,
  dateDescription: false,
  endDate: false,
  dateComplement: false,
  type: false,
  maximumCapacity: false,
  isPublished: false,
  // Ticket purchase
  ticketPurchaseUrl: false,
  // Social media
  websiteUrl: false,
  linkedinUrl: false,
  twitterUrl: false,
  instagramUrl: false,
  facebookUrl: false,
  youtubeUrl: false,
  tiktokUrl: false,
};

export const useEventNew = () => {
  const { setToast } = useToast();
  const { push } = useHistory();
  const { connectGraphQLApi, cancelRequest } = useGraphQLAuth();

  const [initialInputsValues, setInitialInputsValues] =
    useState(initInputsValues);
  const [changeFooter, setChangeFooter] = useState<string[]>([]);
  const [submitButton, setSubmitButton] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const {
    handleSubmit,
    handleChange,
    resetForm,
    setValues: setInputsValues,
    values: inputsValues,
    touched,
    errors,
  } = useFormik({
    initialValues: initInputsValues,
    initialTouched: initTouchedInputsValues,
    validationSchema: Yup.object({
      name: Yup.string().required("Nome é obrigatório"),
      description: Yup.string().optional(),
      organizer: Yup.string().optional(),
      // logoUrl: Yup.string().optional(),
      address: Yup.string().required("Endereço é obrigatório"),
      dateDescription: Yup.string().required("Descrição da data é obrigatória"),
      endDate: Yup.date().optional().nullable(),
      dateComplement: Yup.string().optional(),
      type: Yup.string()
        .required("Tipo do evento é obrigatório")
        .oneOf(["PUBLIC", "PRIVATE"], "Tipo deve ser Publico ou Privado"),
      maximumCapacity: Yup.number()
        .required("Capacidade máxima é obrigatória")
        .min(1, "Capacidade deve ser maior que 0"),
      isPublished: Yup.boolean().optional(),
      mentorshipsEnabled: Yup.boolean().optional(),
      showTicketQrCode: Yup.boolean().optional(),
      // Ticket purchase URL
      ticketPurchaseUrl: Yup.string().url("URL inválida").optional(),
      // Social media URLs - all optional
      websiteUrl: Yup.string().url("URL inválida").optional(),
      linkedinUrl: Yup.string().url("URL inválida").optional(),
      twitterUrl: Yup.string().url("URL inválida").optional(),
      instagramUrl: Yup.string().url("URL inválida").optional(),
      facebookUrl: Yup.string().url("URL inválida").optional(),
      youtubeUrl: Yup.string().url("URL inválida").optional(),
      tiktokUrl: Yup.string().url("URL inválida").optional(),
      // Map fields - all optional
      // mapPdfUrl: Yup.string().url('URL inválida').optional(),
      // mapType: Yup.string().optional(),
    }),
    onSubmit: (_) => {
      sendData();
    },
  });

  /* HOOKS */
  useEffect(() => {
    return () => {
      cancelRequest();
    };
  }, []);

  useEffect(() => {
    const changed = checkChange(inputsValues, initialInputsValues, true);
    setChangeFooter(changed);
  }, [inputsValues, initialInputsValues]);

  useEffect(() => {
    setInputsValues(initialInputsValues);
  }, [initialInputsValues]);

  /* EVENT HANDLERS */
  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    // Handle nested fields (socialMedia and mapPdf)
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      if (parent === "socialMedia") {
        setInputsValues((prev) => ({
          ...prev,
          socialMedia: {
            ...prev.socialMedia,
            [child]: value,
          },
        }));
      }
      return;
    }

    handleChange(event);
  };

  const handleSelectChange = (event: ChangeEvent<HTMLSelectElement>) => {
    handleChange(event);
  };
  const customHandleImageChange = async (file: File) => {
    setImageFile(file);
  };

  const customHandlePdfChange = async (file: File) => {
    setPdfFile(file);
  };

  /* API CALLS */
  const sendData = async () => {
    setSubmitButton(true);
    setIsLoading(true);
    cancelRequest();

    const query = `
      mutation BackofficeCreateEvent($input: EventInput!) {
        backofficeCreateEvent(input: $input) {
          eventId
          name
          description
          organizer
          address
          type
          dateDescription
          endDate
          dateComplement
          maximumCapacity
          isPublished
          mentorshipsEnabled
          showTicketQrCode
          ticketPurchaseUrl
          insertDate
          updateDate
          socialMedia {
            websiteUrl
            linkedinUrl
            twitterUrl
            instagramUrl
            facebookUrl
            youtubeUrl
            tiktokUrl
          }
        }
      }
    `;

    const endDateFormatted = inputsValues.endDate
      ? new Date(inputsValues.endDate).toISOString()
      : "";

    const variables = {
      input: {
        name: inputsValues.name.trim(),
        description: inputsValues.description?.trim() || undefined,
        organizer: inputsValues.organizer?.trim() || undefined,
        address: inputsValues.address.trim(),
        type: inputsValues.type,
        dateDescription: inputsValues.dateDescription.trim(),
        endDate: endDateFormatted,
        dateComplement: inputsValues.dateComplement?.trim() || undefined,
        maximumCapacity: parseInt(inputsValues.maximumCapacity.toString(), 10),
        isPublished: inputsValues.isPublished || false,
        mentorshipsEnabled: inputsValues.mentorshipsEnabled || false,
        showTicketQrCode: inputsValues.showTicketQrCode ?? true,
        ticketPurchaseUrl: inputsValues.ticketPurchaseUrl?.trim() || undefined,
        socialMedia: {
          websiteUrl: inputsValues.socialMedia?.websiteUrl?.trim() || undefined,
          linkedinUrl:
            inputsValues.socialMedia?.linkedinUrl?.trim() || undefined,
          twitterUrl: inputsValues.socialMedia?.twitterUrl?.trim() || undefined,
          instagramUrl:
            inputsValues.socialMedia?.instagramUrl?.trim() || undefined,
          facebookUrl:
            inputsValues.socialMedia?.facebookUrl?.trim() || undefined,
          youtubeUrl: inputsValues.socialMedia?.youtubeUrl?.trim() || undefined,
          tiktokUrl: inputsValues.socialMedia?.tiktokUrl?.trim() || undefined,
        },
      },
    };

    try {
      const response = await connectGraphQLApi({
        query,
        variables,
        errorMessage: "Falha ao criar evento",
        useNewApi: true,
      });

      if (imageFile) {
        await FileService.uploadFile({
          file: imageFile,
          refCollectionId: response.data.backofficeCreateEvent.eventId,
          refCollectionName: "events",
          refCollectionField: "logoId",
          refCollectionFieldType: "string",
        });
      }

      if (pdfFile) {
        await FileService.uploadFile({
          file: pdfFile,
          refCollectionId: response.data.backofficeCreateEvent.eventId,
          refCollectionName: "events",
          refCollectionField: "mapPdfId",
          refCollectionFieldType: "string",
        });
      }

      setToast({
        type: "success",
        message: "Evento criado com sucesso.",
        isActive: true,
        timeToRemoveToast: 3000,
      });

      const responseData = (response as any).data;
      const createdEvent = responseData.backofficeCreateEvent;

      push(`/events/${createdEvent.eventId}`);
    } catch (error: any) {
      console.error("Error creating event:", error);

      // Log the full error object
      console.error("Full error object:", JSON.stringify(error, null, 2));

      // Try to extract more error information
      if (error.networkError) {
        console.error("Network error:", error.networkError);
        console.error("Network error status:", error.networkError.statusCode);
        console.error("Network error result:", error.networkError.result);
      }

      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        console.error("GraphQL errors:", error.graphQLErrors);
        error.graphQLErrors.forEach((gqlError: any, index: number) => {
          console.error(`GraphQL Error ${index + 1}:`, {
            message: gqlError.message,
            extensions: gqlError.extensions,
            path: gqlError.path,
          });
        });

        const errorMessage =
          error.graphQLErrors[0].message || "Erro ao criar evento";
        setToast({
          type: "error",
          message: errorMessage,
          isActive: true,
          timeToRemoveToast: 5000,
        });
      } else if (error.message) {
        // Check if it's a connection error
        console.error("Error message:", error.message);
        console.error("Error type:", error.errorType);

        setToast({
          type: "error",
          message: error.message || "Falha ao criar evento. Tente novamente.",
          isActive: true,
          timeToRemoveToast: 5000,
        });
      } else {
        // Unknown error
        console.error("Unknown error structure:", error);
        setToast({
          type: "error",
          message: "Erro desconhecido ao criar evento.",
          isActive: true,
          timeToRemoveToast: 5000,
        });
      }
    } finally {
      setSubmitButton(false);
      setIsLoading(false);
    }
  };

  return {
    handleInputChange,
    handleSelectChange,
    inputsValues,
    touched,
    errors,
    changeFooter,
    submitButton,
    handleSubmit,
    hasError,
    isLoading,
    customHandleImageChange,
    customHandlePdfChange,
  };
};
